// src/modals/MilestonesModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Tab,
  Tabs
} from '@mui/material';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import { formatDate } from '../utils/constants';

/**
 * MilestonesModal displays detailed information about all project milestones,
 * including their dates, status, and dependencies.
 */
const MilestonesModal = ({ open, onClose, projectName, projectId, milestones = [] }) => {
  const [tabValue, setTabValue] = useState(0);
  
  // Simulate some milestone data for the placeholder
  const placeholderMilestones = milestones.length > 0 ? milestones : [
    {
      name: 'Project Kickoff',
      date: '2024-12-01',
      status: 'Completed',
      description: 'Initial project kickoff meeting with client stakeholders',
      completed: true,
      progress: 100
    },
    {
      name: 'Requirements Gathering',
      date: '2025-01-15',
      status: 'Completed',
      description: 'Document and approve project requirements',
      completed: true,
      progress: 100
    },
    {
      name: 'Design Phase Complete',
      date: '2025-02-28',
      status: 'In Progress',
      description: 'Complete UI/UX design and technical architecture',
      completed: false,
      progress: 75
    },
    {
      name: 'Development Phase 1',
      date: '2025-04-15',
      status: 'Not Started',
      description: 'Complete core functionality development',
      completed: false,
      progress: 0
    },
    {
      name: 'User Acceptance Testing',
      date: '2025-05-30',
      status: 'Not Started',
      description: 'Client review and approval of deliverables',
      completed: false,
      progress: 0
    }
  ];
  
  // Count milestones by status
  const completedCount = placeholderMilestones.filter(m => 
    m.status?.toLowerCase() === 'completed' || m.completed === true
  ).length;
  
  const inProgressCount = placeholderMilestones.filter(m => 
    m.status?.toLowerCase().includes('progress') || 
    (m.progress > 0 && m.progress < 100)
  ).length;
  
  const pendingCount = placeholderMilestones.filter(m => 
    m.status?.toLowerCase().includes('not started') || 
    m.progress === 0
  ).length;
  
  // Calculate overall milestone completion percentage
  const overallProgress = placeholderMilestones.length > 0 
    ? placeholderMilestones.reduce((sum, m) => sum + (m.progress || 0), 0) / placeholderMilestones.length 
    : 0;
  
  // Get status color
  const getStatusColor = (status) => {
    if (!status) return '#757575';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('complete')) return '#03DAC6';
    if (statusLower.includes('progress')) return '#FFCF44';
    if (statusLower.includes('not started') || statusLower.includes('pending')) return '#757575';
    if (statusLower.includes('risk') || statusLower.includes('delay')) return '#CF6679';
    
    return '#757575';
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Filter milestones based on tab selection
  const filteredMilestones = tabValue === 0 
    ? placeholderMilestones
    : tabValue === 1 
      ? placeholderMilestones.filter(m => m.status?.toLowerCase() === 'completed' || m.completed === true)
      : tabValue === 2
        ? placeholderMilestones.filter(m => m.status?.toLowerCase().includes('progress'))
        : placeholderMilestones.filter(m => m.status?.toLowerCase().includes('not started') || m.progress === 0);

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={`Project Milestones: ${projectName}`}
      subtitle={`Project ID: ${projectId} | Total Milestones: ${placeholderMilestones.length}`}
    >
      {/* Summary cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Overall Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: '#BB86FC'
                    }}
                  >
                    {overallProgress.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={overallProgress} 
                  sx={{ 
                    height: 10,
                    borderRadius: 5,
                    bgcolor: '#111',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      bgcolor: '#BB86FC'
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Completed
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#03DAC6'
                  }}
                >
                  {completedCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  of {placeholderMilestones.length} milestones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  In Progress
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#FFCF44'
                  }}
                >
                  {inProgressCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  of {placeholderMilestones.length} milestones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Pending
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#757575'
                  }}
                >
                  {pendingCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  of {placeholderMilestones.length} milestones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Tabs to filter milestones */}
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab label="All Milestones" />
          <Tab label="Completed" />
          <Tab label="In Progress" />
          <Tab label="Not Started" />
        </Tabs>
      </Box>
      
      {/* Milestones table */}
      {filteredMilestones.length > 0 ? (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Milestone</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMilestones.map((milestone, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: '#64B5F6', fontWeight: 'bold' }}>
                    {milestone.name || milestone.title || `Milestone ${index + 1}`}
                  </TableCell>
                  <TableCell>
                    {milestone.date ? formatDate(new Date(milestone.date)) : 'Not set'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={milestone.status || 'Pending'} 
                      size="small"
                      sx={{ 
                        bgcolor: `${getStatusColor(milestone.status)}20`,
                        color: getStatusColor(milestone.status),
                        fontSize: '0.65rem'
                      }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 100, mr: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={milestone.progress || 0} 
                          sx={{ 
                            height: 8,
                            borderRadius: 5,
                            bgcolor: '#111',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              bgcolor: milestone.progress >= 100 ? '#03DAC6' :
                                      milestone.progress > 0 ? '#FFCF44' : '#757575'
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2">
                        {milestone.progress || 0}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {milestone.description || 'No description provided'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          color: 'text.secondary',
          bgcolor: 'background.paper',
          borderRadius: 1,
          mb: 3
        }}>
          <Typography variant="body1">
            No milestones match the selected filter.
          </Typography>
        </Box>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Milestone data last updated: {new Date().toLocaleString()}
        </Typography>
      </Box>
    </BaseExpandedCard>
  );
};

MilestonesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectName: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  milestones: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    status: PropTypes.string,
    description: PropTypes.string,
    completed: PropTypes.bool,
    progress: PropTypes.number
  }))
};

export default MilestonesModal;
